// Manages <head> meta tags for a single page: title, description, canonical,
// Open Graph and Twitter card. Google renders JS since 2019, so a
// client-side hook is enough for indexing. Facebook, LinkedIn and X read
// the same tags from the DOM when their user agents render the page.
//
// Usage:
//   useMetaTags({
//     title: "The Precision Yield Protocol",
//     description: "Yield is the single most under-measured number...",
//     url: "https://www.culinairespecialist.com/blog/precision-yield",
//     image: "https://.../hero.jpg",
//     type: "article",
//   });
import { useEffect } from "react";

function ensureTag(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("meta") ? "meta" : "link");
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === "") el.removeAttribute(k);
    else el.setAttribute(k, v);
  }
  return el;
}

function ensureMeta(name, content, useProperty = false) {
  const attr = useProperty ? "property" : "name";
  const selector = `meta[${attr}="${name}"]`;
  return ensureTag(selector, { [attr]: name, content });
}

function ensureLink(rel, href) {
  const selector = `link[rel="${rel}"]`;
  return ensureTag(selector, { rel, href });
}

export function useMetaTags({ title, description, url, image, type = "website", siteName = "CulinaireSpecialist" }) {
  useEffect(() => {
    if (!title && !description) return;
    const prev = {
      title: document.title,
    };
    if (title) document.title = title;

    const nodes = [];
    if (description) nodes.push(ensureMeta("description", description));
    nodes.push(ensureMeta("og:title", title || document.title, true));
    if (description) nodes.push(ensureMeta("og:description", description, true));
    nodes.push(ensureMeta("og:type", type, true));
    nodes.push(ensureMeta("og:site_name", siteName, true));
    if (url) {
      nodes.push(ensureMeta("og:url", url, true));
      nodes.push(ensureLink("canonical", url));
    }
    if (image) {
      nodes.push(ensureMeta("og:image", image, true));
      nodes.push(ensureMeta("twitter:image", image));
    }
    nodes.push(ensureMeta("twitter:card", image ? "summary_large_image" : "summary"));
    nodes.push(ensureMeta("twitter:title", title || document.title));
    if (description) nodes.push(ensureMeta("twitter:description", description));

    return () => {
      document.title = prev.title;
    };
  }, [title, description, url, image, type, siteName]);
}
